import React, { useEffect, useRef, useState } from 'react';
import {
  View, TextInput, TouchableOpacity, FlatList, StyleSheet,
  KeyboardAvoidingView, Platform, Modal, ActivityIndicator, Alert, ScrollView,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { ArrowLeft, Send, MapPin, Vote } from 'lucide-react-native';
import * as Haptics from 'expo-haptics';
import { useTranslation } from 'react-i18next';
import { Text } from '@/components';
import LottieLoader from '@/components/LottieLoader';
import theme from '@/constants/colors';
import ChatMessageCard from '@/components/ChatMessageCard';
import TrailMap from '@/components/TrailMap';
import { useAuth } from '@/context/AuthContext';
import { useUserLocation } from '@/hooks/useUserLocation';
import { useGroupMessages, useSendGroupMessage, useDeleteGroupMessage, useEditGroupMessage, flattenChatPages, useChatWsEvents } from '@/lib/queries/groupChat';
import { logEvent } from '@/lib/analytics';
import styles from './chat.styles';

export default function GroupChatScreen({ embedded = false }: { embedded?: boolean } = {}) {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { session } = useAuth();
  const token = session?.accessToken;
  const { t } = useTranslation();
  const router = useRouter();
  const groupId = typeof id === 'string' ? id : undefined;
  const { locationCoords } = useUserLocation();
  const messagesQuery = useGroupMessages(token, groupId);
  useChatWsEvents(token, groupId);
  const sendMutation = useSendGroupMessage(token, groupId);
  const deleteMutation = useDeleteGroupMessage(token, groupId);
  const editMutation = useEditGroupMessage(token, groupId);
  const [editModal, setEditModal] = useState<{ message: (typeof messages)[number] | null; body: string; options: string[] } | null>(null);

  const [draft, setDraft] = useState('');
  const [pollModalVisible, setPollModalVisible] = useState(false);
  const [pinModalVisible, setPinModalVisible] = useState(false);
  const [pickedPin, setPickedPin] = useState<{ latitude: number; longitude: number } | null>(null);
  const [meetLabel, setMeetLabel] = useState('');
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState<string[]>(['', '']);
  const listRef = useRef<FlatList>(null);
  const insets = useSafeAreaInsets();

  const messages = flattenChatPages(messagesQuery.data);

  useEffect(() => {
    listRef.current?.scrollToOffset({ offset: 0, animated: true });
  }, [messages.length]);

  const send = async (payload: { type: string; body?: string | null; payload?: Record<string, any> }) => {
    if (true) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    setDraft('');
    await sendMutation.mutateAsync(payload);
  };

  const sendText = async () => {
    const text = draft.trim();
    if (!text) return;
    logEvent('chat_message_sent', { type: 'text' });
    await send({ type: 'text', body: text });
  };

  const openPinPicker = () => {
    if (true) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    setPickedPin(null);
    setMeetLabel('');
    setPinModalVisible(true);
  };

  const sendPin = () => {
    if (!pickedPin) return;
    setPinModalVisible(false);
    logEvent('chat_message_sent', { type: 'location' });
    logEvent('chat_meetpoint_sent', { lat: pickedPin.latitude });
    send({
      type: 'location',
      payload: { lat: pickedPin.latitude, lng: pickedPin.longitude, label: meetLabel.trim() || t('packChat.meetPointDefault') },
    });
  };

  const sendPoll = () => {
    const question = pollQuestion.trim();
    const options = pollOptions.map((o) => o.trim()).filter(Boolean);
    if (!question || options.length < 2) return;
    setPollModalVisible(false);
    setPollQuestion('');
    setPollOptions(['', '']);
    logEvent('chat_message_sent', { type: 'poll' });
    logEvent('poll_created', { options: options.length });
    send({ type: 'poll', payload: { question, options } });
  };

  const saveEdit = async () => {
    if (!editModal?.message) return;
    const body = editModal.body.trim();
    if (!body) return;
    const isPoll = editModal.message.type === 'poll' && editModal.message.poll;
    const options =
      isPoll && editModal.message.poll!.totalVotes === 0
        ? editModal.options.map((o) => o.trim()).filter(Boolean)
        : undefined;
    if (isPoll && options && options.length < 2) return;
    await editMutation.mutateAsync({ messageId: editModal.message.id, body, options });
    setEditModal(null);
  };

  const renderItem = ({ item }: { item: (typeof messages)[number] }) => (
    <ChatMessageCard
      message={item}
      token={token}
      groupId={groupId ?? ''}
      onDelete={(mid) => deleteMutation.mutate(mid)}
      onEdit={(m) =>
        setEditModal({
          message: m,
          body: m.type === 'poll' ? m.poll?.question ?? m.body ?? '' : m.body ?? '',
          options: m.type === 'poll' ? m.payload?.options ?? [] : [],
        })
      }
    />
  );

  return (
    <SafeAreaView style={styles.container} edges={embedded ? [] : ['top']}>
      {!embedded && (
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={() => router.back()}>
          <ArrowLeft size={22} color={theme.accentPrimary} />
        </TouchableOpacity>
        <Text style={styles.title}>{t('packChat.title')}</Text>
        <View style={{ width: 22 }} />
      </View>
      )}

      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        {messagesQuery.isLoading ? (
          <View style={styles.loading}><LottieLoader size={120} /></View>
        ) : messagesQuery.isError ? (
          <View style={styles.loading}>
            <Text style={styles.errorText}>{messagesQuery.error?.message ?? 'error'}</Text>
            <TouchableOpacity style={styles.retryButton} onPress={() => messagesQuery.refetch()}>
              <Text style={styles.retryText}>{t('common.retry')}</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <FlatList
            ref={listRef}
            data={messages}
            keyExtractor={(m) => m.id}
            renderItem={renderItem}
            inverted
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={[styles.listContent, { paddingBottom: 6 }]}
            ListFooterComponent={
              messagesQuery.isFetchingNextPage ? <ActivityIndicator style={{ marginVertical: 10 }} /> : null
            }
            onEndReachedThreshold={0.4}
            onEndReached={() => {
              if (messagesQuery.hasNextPage && !messagesQuery.isFetchingNextPage) {
                messagesQuery.fetchNextPage();
              }
            }}
          />
        )}

        <View style={[styles.composerRow, { paddingBottom: (insets.bottom || 8) + 6 }]}>
          <TouchableOpacity
            style={styles.attachButton}
            onPress={openPinPicker}
          >
            <MapPin size={20} color={theme.accentPrimary} />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.attachButton}
            onPress={() => { if (true) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light); setPollModalVisible(true); }}
          >
            <Vote size={20} color={theme.accentPrimary} />
          </TouchableOpacity>
          <TextInput
            style={styles.input}
            value={draft}
            onChangeText={setDraft}
            placeholder={t('packChat.placeholder')}
            placeholderTextColor={theme.textMuted}
            multiline
          />
          <TouchableOpacity style={styles.sendButton} onPress={sendText} disabled={!draft.trim()}>
            <Send size={20} color={draft.trim() ? theme.accentPrimary : theme.textMuted} />
          </TouchableOpacity>
        </View>
      </KeyboardAvoidingView>

      {/* edit message */}
      {editModal && (
      <Modal visible transparent animationType="fade" onRequestClose={() => setEditModal(null)}>
        <KeyboardAvoidingView style={styles.modalOverlay} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
          <TouchableOpacity style={styles.modalOverlaySpacer} activeOpacity={1} onPress={() => setEditModal(null)} />
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>
              {editModal.message?.type === 'poll' ? t('packChat.editPoll') : t('packChat.editTitle')}
            </Text>
            <TextInput
              style={[styles.input, styles.pollQuestionInput]}
              value={editModal.body}
              onChangeText={(v) => setEditModal({ ...editModal, body: v })}
              multiline
              autoFocus
            />
            {editModal.message?.type === 'poll' && editModal.message.poll && editModal.message.poll.totalVotes === 0 &&
              editModal.options.map((opt, i) => (
                <View key={i} style={styles.pollOptionRow}>
                  <TextInput
                    style={[styles.input, styles.pollOptionInput]}
                    value={opt}
                    onChangeText={(v) =>
                      setEditModal((cur) => (cur ? { ...cur, options: cur.options.map((o, j) => (j === i ? v : o)) } : cur))
                    }
                    placeholder={t('packChat.pollOption', { n: i + 1 })}
                    placeholderTextColor={theme.textMuted}
                  />
                  {editModal.options.length > 2 && (
                    <TouchableOpacity onPress={() => setEditModal((cur) => (cur ? { ...cur, options: cur.options.filter((_, j) => j !== i) } : cur))}>
                      <Text style={styles.pollRemove}>✕</Text>
                    </TouchableOpacity>
                  )}
                </View>
              ))}
            {editModal.message?.type === 'poll' && (editModal.message.poll?.totalVotes ?? 0) > 0 && (
              <Text style={styles.pollMeta}>{t('packChat.optionsLocked')}</Text>
            )}
            <View style={{ flexDirection: 'row', gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
              <TouchableOpacity style={styles.pollAdd} onPress={() => setEditModal(null)}>
                <Text style={styles.pollAddText}>{t('common.cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.sendButtonFull, { flex: 1, marginTop: 0, opacity: editModal.body.trim() ? 1 : 0.4 }]}
                disabled={!editModal.body.trim()}
                onPress={saveEdit}
              >
                <Text style={styles.sendButtonFullText}>{t('packChat.saveEdit')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>
      )}

      {/* meet-point picker: tap the map to drop the pin.
          IMPORTANT: body renders only while visible — a hidden Modal still
          mounts its children, and a maplibre view inside a hidden modal froze
          the whole chat render. */}
      {pinModalVisible && (
      <Modal visible={pinModalVisible} transparent animationType="fade" onRequestClose={() => setPinModalVisible(false)}>
        <KeyboardAvoidingView
          style={styles.modalOverlay}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        >
          <TouchableOpacity style={styles.modalOverlaySpacer} activeOpacity={1} onPress={() => setPinModalVisible(false)} />
          <ScrollView style={[styles.pinScroll]} contentContainerStyle={styles.pinScrollContent} keyboardShouldPersistTaps="handled">
          <View style={[styles.modalCard, styles.wideCard]}>
            <Text style={styles.modalTitle}>{t('packChat.pickSpot')}</Text>
            <TrailMap
              initialRegion={locationCoords
                ? { latitude: locationCoords.latitude, longitude: locationCoords.longitude, latitudeDelta: 0.02, longitudeDelta: 0.03 }
                : undefined}
              showsUserLocation
              onMapPress={(loc) => setPickedPin(loc)}
              pickedPin={pickedPin}
              style={styles.pinMap}
            />
            <View style={styles.pollOptionRow}>
              <TextInput
                style={[styles.input, styles.pollOptionInput, styles.pollOptionInput]}
                value={meetLabel}
                onChangeText={setMeetLabel}
                placeholder={pickedPin ? t('packChat.spotLabel') : t('packChat.tapMapFirst')}
                placeholderTextColor={theme.textMuted}
              />
            </View>
            <View style={{ flexDirection: 'row', gap: 8, marginTop: 10 }}>
              <TouchableOpacity style={styles.pollAdd} onPress={() => setPinModalVisible(false)}>
                <Text style={styles.pollAddText}>{t('common.cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.sendButtonFull, { flex: 1, marginTop: 0, opacity: pickedPin ? 1 : 0.4 }]}
                disabled={!pickedPin}
                onPress={sendPin}
              >
                <Text style={styles.sendButtonFullText}>
                  {pickedPin ? t('packChat.sharePinned') : t('packChat.tapMapFirst')}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
          </ScrollView>
        </KeyboardAvoidingView>
      </Modal>
      )}

      {/* poll composer — its own KeyboardAvoidingView: modals render outside
          the page's KAV, so without this the keyboard covers the fields */}
      {pollModalVisible && (
      <Modal visible={pollModalVisible} transparent animationType="fade" onRequestClose={() => setPollModalVisible(false)}>
        <KeyboardAvoidingView
          style={styles.modalOverlay}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        >
          <TouchableOpacity style={styles.modalOverlaySpacer} activeOpacity={1} onPress={() => setPollModalVisible(false)} />
          <ScrollView style={styles.pollScroll} keyboardShouldPersistTaps="handled" contentContainerStyle={styles.pollScrollContent}>
            <TouchableOpacity style={styles.modalCard} activeOpacity={1} onPress={() => {}}>
              <Text style={styles.modalTitle}>{t('packChat.createPoll')}</Text>
              <TextInput
                style={[styles.input, styles.pollQuestionInput]}
                value={pollQuestion}
                onChangeText={setPollQuestion}
                placeholder={t('packChat.pollQuestion')}
                placeholderTextColor={theme.textMuted}
              />
              {pollOptions.map((opt, i) => (
                <View key={i} style={styles.pollOptionRow}>
                  <TextInput
                    style={[styles.input, styles.pollOptionInput]}
                    value={opt}
                    onChangeText={(v) => setPollOptions((prev) => prev.map((o, j) => (j === i ? v : o)))}
                    placeholder={t('packChat.pollOption', { n: i + 1 })}
                    placeholderTextColor={theme.textMuted}
                  />
                  {pollOptions.length > 2 && (
                    <TouchableOpacity onPress={() => setPollOptions((prev) => prev.filter((_, j) => j !== i))}>
                      <Text style={styles.pollRemove}>✕</Text>
                    </TouchableOpacity>
                  )}
                </View>
              ))}
              {pollOptions.length < 20 && (
                <TouchableOpacity style={styles.pollAdd} onPress={() => setPollOptions((prev) => [...prev, ''])}>
                  <Text style={styles.pollAddText}>{t('packChat.addOption')}</Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity
                style={styles.sendButtonFull}
                disabled={!pollQuestion.trim() || pollOptions.filter((o) => o.trim()).length < 2}
                onPress={sendPoll}
              >
                <Text style={styles.sendButtonFullText}>{t('packChat.pollPost')}</Text>
              </TouchableOpacity>
            </TouchableOpacity>
          </ScrollView>
        </KeyboardAvoidingView>
      </Modal>
      )}
    </SafeAreaView>
  );}